import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Folder, File as FileIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';

/* ── Delete confirmation ───────────────────────────────────────────────── */

/** How many names a multi-delete lists before summarising the rest. */
export const DELETE_LIST_MAX = 8;

/**
 * Confirm a delete — in a modal, like every other decision in the app, rather
 * than the inline bar it used to be, which sat in the pane's toolbar area and
 * was easy to miss or click through.
 *
 * **It names what is going.** "Delete 2 selected items?" asks the user to
 * trust that the selection is what they think it is, and a Shift-range or a
 * Ctrl+A makes that easy to get wrong. The first few names are listed and the
 * rest counted.
 *
 * **Cancel holds initial focus**, so an Enter pressed out of habit refuses. The
 * one irreversible action here must never be the default.
 */
export function DeleteConfirmModal({ entries, onClose, onConfirm }) {
  const { t } = useTranslation();
  const cancelRef = useRef(null);
  const many = entries.length > 1;
  const shown = entries.slice(0, DELETE_LIST_MAX);
  const rest = entries.length - shown.length;

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  return (
    <Modal isOpen onClose={onClose} title={t('common.delete')} type="warning">
      <div className="space-y-3">
        <p className="ftp-delete-question">
          {many
            ? t('ftp.deleteConfirmMany', { count: entries.length })
            : t('ftp.deleteConfirm', { name: entries[0]?.name })}
        </p>
        {many && (
          <ul className="ftp-delete-list">
            {shown.map((e) => (
              <li key={e.path} className="ftp-delete-item">
                {e.isDir ? <Folder size={12} /> : <FileIcon size={12} />}
                <span className="ftp-delete-name">{e.name}</span>
              </li>
            ))}
            {rest > 0 && (
              <li className="ftp-delete-more">{t('ftp.deleteAndMore', { count: rest })}</li>
            )}
          </ul>
        )}
        {entries.some((e) => e.isDir) && (
          // About ANY folder in the set: deleting five things one of which is a
          // directory is still a recursive delete.
          <p className="ftp-modal-note">{t('ftp.deleteRecursiveNote')}</p>
        )}
        <div className="modal-footer-row">
          <button
            type="button"
            ref={cancelRef}
            onClick={onClose}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
          >
            {many ? `${t('common.delete')} (${entries.length})` : t('common.delete')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
